import { ArrowLeft, ArrowRight } from '@ethsign/icons';
import { Button } from '@ethsign/ui';
import { Table } from '@tanstack/react-table';

import React from 'react';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
}

export function DataTablePagination<TData>({ table }: DataTablePaginationProps<TData>) {
  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;
  const pagination = (selected: number, pageCount: number) => {
    const items = [];
    const pageRangeDisplayed = 3;
    const marginPagesDisplayed = 2;
    const breakLabel = '...';
    const createPageView = (index: number) => (
      <span
        key={index}
        onClick={() => {
          table.setPageIndex(index);
        }}
        className={`text-center leading-10 cursor-pointer w-10 h-10 rounded-md ${
          pageIndex === index ? 'bg-gray-50' : ''
        }`}
      >
        {index + 1}
      </span>
    );
    if (pageCount <= pageRangeDisplayed) {
      for (let index = 0; index < pageCount; index++) {
        items.push(createPageView(index));
      }
    } else {
      let leftSide = pageRangeDisplayed / 2;
      let rightSide = pageRangeDisplayed - leftSide;

      if (selected > pageCount - pageRangeDisplayed / 2) {
        rightSide = pageCount - selected;
        leftSide = pageRangeDisplayed - rightSide;
      } else if (selected < pageRangeDisplayed / 2) {
        leftSide = selected;
        rightSide = pageRangeDisplayed - leftSide;
      }

      let index;
      let breakView;

      // First pass: process the pages or breaks to display (or not).
      const pagesBreaking: any = [];
      for (index = 0; index < pageCount; index++) {
        const page = index + 1;

        // If the page index is lower than the margin defined,
        // the page has to be displayed on the left side of
        // the pagination.
        if (page <= marginPagesDisplayed) {
          pagesBreaking.push({
            type: 'page',
            index,
            display: createPageView(index)
          });
          continue;
        }

        if (page > pageCount - marginPagesDisplayed) {
          pagesBreaking.push({
            type: 'page',
            index,
            display: createPageView(index)
          });
          continue;
        }

        const adjustedRightSide = selected === 0 && pageRangeDisplayed > 1 ? rightSide - 1 : rightSide;

        if (index >= selected - leftSide && index <= selected + adjustedRightSide) {
          pagesBreaking.push({
            type: 'page',
            index,
            display: createPageView(index)
          });
          continue;
        }

        if (
          breakLabel &&
          pagesBreaking.length > 0 &&
          pagesBreaking[pagesBreaking.length - 1].display !== breakView &&
          // We do not show break if only one active page is displayed.
          (pageRangeDisplayed > 0 || marginPagesDisplayed > 0)
        ) {
          breakView = (
            <span key={index} className="text-center leading-10 cursor-pointer w-10 h-10">
              {breakLabel}
            </span>
          );
          pagesBreaking.push({ type: 'break', index, display: breakView });
        }
      }
      // Second pass: we remove breaks containing one page to the actual page.
      pagesBreaking.forEach((pageElement: any, i: number) => {
        let actualPageElement = pageElement;
        // 1 2 3 4 5 6 7 ... 9 10
        //         |
        // 1 2 ... 4 5 6 7 8 9 10
        //             |
        // The break should be replaced by the page.
        if (
          pageElement.type === 'break' &&
          pagesBreaking[i - 1] &&
          pagesBreaking[i - 1].type === 'page' &&
          pagesBreaking[i + 1] &&
          pagesBreaking[i + 1].type === 'page' &&
          pagesBreaking[i + 1].index - pagesBreaking[i - 1].index <= 2
        ) {
          actualPageElement = {
            type: 'page',
            index: pageElement.index,
            display: createPageView(pageElement.index)
          };
        }
        // We add the displayed elements in the same pass, to avoid another iteration.
        items.push(actualPageElement.display);
      });
    }

    return items;
  };

  return (
    <div className="flex items-center justify-between px-6 h-[68px] flex-row border-gray-200 border-t">
      <Button
        variant="secondary-gray"
        size="sm"
        onClick={() => table.previousPage()}
        disabled={!table.getCanPreviousPage()}
      >
        <ArrowLeft color="#344054" size={16} />
        <span className="ml-2">Previous</span>
      </Button>
      <div className="flex items-center ">{pagination(pageIndex, pageCount)}</div>
      <Button variant="secondary-gray" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
        <span className="mr-2">Next</span>
        <ArrowRight color="#344054" size={16} />
      </Button>
    </div>
  );
}
